import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { format, isSameDay, parseISO } from 'date-fns';
import { CaretRight, Play, Trophy, ClockCounterClockwise } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function History() {
  const navigate = useNavigate();
  const [workouts, setWorkouts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/workouts/mine`, { withCredentials: true });
        setWorkouts(data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, []);

  // Group by day
  const groups = useMemo(() => {
    const byDay = [];
    workouts.forEach((w) => {
      const d = w.started_at ? parseISO(w.started_at) : new Date();
      const existing = byDay.find((g) => isSameDay(g.date, d));
      if (existing) existing.items.push(w);
      else byDay.push({ date: d, items: [w] });
    });
    return byDay;
  }, [workouts]);

  const startAgain = async (w) => {
    if (!w.routine_id) return;
    try {
      const { data } = await axios.post(
        `${API_URL}/workouts/start`,
        { routine_id: w.routine_id },
        { withCredentials: true }
      );
      navigate(`/session/${data.id}`);
    } catch (e) {
      alert('Could not start workout — is another one already running?');
    }
  };

  if (loading) {
    return (
      <div className="mobile-container min-h-screen flex items-center justify-center">
        <div className="animate-pulse-soft text-6xl font-black font-heading text-graphite">B</div>
      </div>
    );
  }

  return (
    <div className="mobile-container min-h-screen">
      <div className="content-wrapper px-5 pt-10">
        <div className="mb-6 animate-slide-up">
          <p className="text-xs uppercase tracking-[0.24em] text-graphite/50 font-semibold mb-1">
            Every Session
          </p>
          <h1 className="text-5xl font-black font-heading uppercase tracking-tight text-graphite leading-none">
            History
          </h1>
        </div>

        {workouts.length === 0 ? (
          <div className="ios-card p-8 text-center">
            <ClockCounterClockwise size={40} weight="fill" className="text-graphite/30 mx-auto mb-3" />
            <h3 className="text-xl font-black font-heading uppercase text-graphite mb-2">
              Nothing Logged Yet
            </h3>
            <p className="text-sm text-graphite/60">
              Finish your first session and it'll live here forever.
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {groups.map((g) => (
              <div key={g.date.toISOString()}>
                <p className="text-[10px] uppercase tracking-[0.24em] font-bold text-graphite/50 mb-2 px-1">
                  {format(g.date, 'EEEE, MMM d, yyyy')}
                </p>
                <div className="space-y-2">
                  {g.items.map((w) => (
                    <WorkoutRow key={w.id} workout={w} onStartAgain={() => startAgain(w)} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
</div>
  );
}

function WorkoutRow({ workout, onStartAgain }) {
  const navigate = useNavigate();
  const inProgress = !workout.finished_at;
  const tapDetail = () => {
    if (inProgress) navigate(`/session/${workout.id}`);
    else navigate(`/compare/${workout.id}`);
  };
  return (
    <div className="ios-card overflow-hidden" data-testid={`history-row-${workout.id}`}>
      <button
        onClick={tapDetail}
        className="w-full text-left px-4 py-4 flex items-center gap-3 hover:bg-graphite/5"
        data-testid={`history-open-${workout.id}`}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <p className="text-base font-black font-heading uppercase text-graphite truncate">
              {workout.routine_name || 'Session'}
            </p>
            {inProgress && (
              <span className="text-[9px] uppercase tracking-[0.18em] font-bold text-userRed bg-userRed/10 px-1.5 py-0.5 rounded-ios-pill">
                Running
              </span>
            )}
            {workout.personal_bests > 0 && (
              <span className="flex items-center gap-0.5 text-[9px] uppercase tracking-[0.18em] font-bold text-userRed bg-userRed/10 px-1.5 py-0.5 rounded-ios-pill">
                <Trophy size={10} weight="fill" />
                {workout.personal_bests}
              </span>
            )}
          </div>
          <p className="text-[10px] uppercase tracking-[0.18em] text-graphite/50 font-semibold">
            {workout.started_at && format(parseISO(workout.started_at), 'h:mm a')}
            {' · '}
            {workout.total_sets || 0} sets · {Math.round(workout.total_volume || 0).toLocaleString()}kg
          </p>
        </div>
        <CaretRight size={16} weight="bold" className="text-graphite/30" />
      </button>
      {workout.routine_id && !inProgress && (
        <button
          onClick={onStartAgain}
          className="w-full h-11 border-t border-hairline text-[11px] uppercase tracking-[0.18em] font-bold text-userRed hover:bg-userRed/8 flex items-center justify-center gap-2"
          data-testid={`history-start-again-${workout.id}`}
        >
          <Play size={12} weight="fill" />
          Start Again
        </button>
      )}
    </div>
  );
}
